/**
 * useGridState.js
 * ───────────────
 * Custom hook managing grid view state: active mode, column visibility,
 * dirty tracking, sort state, saved views, and localStorage persistence.
 *
 * Screen-aware: all localStorage keys incorporate the screen name so
 * lifecycle and asset_registry grids maintain independent configs.
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { LIFECYCLE_COLUMNS } from './lifecycleColumnManifest';

/* ── localStorage key helpers (screen-aware) ──────────────────────────── */
function getModeKey(screen, callerEmail) {
  return `tmg_grid_mode_${screen}_${callerEmail || 'default'}`;
}

function getColVisKey(screen, callerEmail) {
  return `tmg_grid_columns_${screen}_${callerEmail || 'default'}`;
}

function getSavedViewsKey(screen, callerEmail) {
  return `tmg_grid_savedviews_${screen}_${callerEmail || 'default'}`;
}

function loadMode(screen, callerEmail) {
  try {
    const v = localStorage.getItem(getModeKey(screen, callerEmail));
    if (v === 'grid' || v === 'detail') return v;
  } catch { /* ignore */ }
  return 'detail';
}

function saveMode(screen, callerEmail, mode) {
  try { localStorage.setItem(getModeKey(screen, callerEmail), mode); } catch { /* ignore */ }
}

function loadColumnVisibility(screen, callerEmail, columns) {
  try {
    const raw = localStorage.getItem(getColVisKey(screen, callerEmail));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch { /* ignore */ }
  const defaults = {};
  for (const col of columns) {
    defaults[col.key] = true;
  }
  return defaults;
}

function saveColumnVisibility(screen, callerEmail, vis) {
  try { localStorage.setItem(getColVisKey(screen, callerEmail), JSON.stringify(vis)); } catch { /* ignore */ }
}

function loadSavedViews(screen, callerEmail) {
  try {
    const raw = localStorage.getItem(getSavedViewsKey(screen, callerEmail));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return [];
}

function persistSavedViews(screen, callerEmail, views) {
  try { localStorage.setItem(getSavedViewsKey(screen, callerEmail), JSON.stringify(views)); } catch { /* ignore */ }
}

/* ── Sort engine (pure function) ──────────────────────────────────────── */
const COL_TYPE_MAP = {};
for (const col of LIFECYCLE_COLUMNS) {
  COL_TYPE_MAP[col.key] = col.type;
}

export function sortRecords(records, sortKeys, colTypeOverrides) {
  if (!sortKeys || sortKeys.length === 0 || !records || records.length === 0) {
    return records;
  }

  const typeMap = colTypeOverrides || COL_TYPE_MAP;

  const sorted = [...records];
  sorted.sort((a, b) => {
    for (const { field, direction } of sortKeys) {
      const av = a[field];
      const bv = b[field];

      const aNull = av == null || av === '';
      const bNull = bv == null || bv === '';
      if (aNull && bNull) continue;
      if (aNull) return 1;
      if (bNull) return -1;

      const colType = typeMap[field] || 'text';
      let cmp = 0;

      if (colType === 'number') {
        cmp = Number(av) - Number(bv);
      } else if (colType === 'boolean') {
        cmp = (av === bv) ? 0 : av ? 1 : -1;
      } else if (colType === 'date') {
        cmp = String(av).localeCompare(String(bv));
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
      }

      if (cmp !== 0) {
        return direction === 'desc' ? -cmp : cmp;
      }
    }
    return 0;
  });

  return sorted;
}

/* ── Hook ─────────────────────────────────────────────────────────────── */
export function useGridState(callerEmail, screen = 'lifecycle', columns = LIFECYCLE_COLUMNS) {
  /* ── Active mode ────────────────────────────────────────────────────── */
  const [activeMode, setActiveModeRaw] = useState(() => loadMode(screen, callerEmail));

  const setActiveMode = useCallback((mode) => {
    setActiveModeRaw(mode);
    saveMode(screen, callerEmail, mode);
  }, [screen, callerEmail]);

  /* ── Column visibility ──────────────────────────────────────────────── */
  const [columnVisibility, setColumnVisibility] = useState(() => loadColumnVisibility(screen, callerEmail, columns));

  const loadingViewRef = useRef(false);

  const toggleColumn = useCallback((key) => {
    setColumnVisibility(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveColumnVisibility(screen, callerEmail, next);
      return next;
    });
    if (!loadingViewRef.current) setActiveViewId(null);
  }, [screen, callerEmail]);

  const showAllColumns = useCallback(() => {
    const all = {};
    for (const col of columns) {
      all[col.key] = true;
    }
    setColumnVisibility(all);
    saveColumnVisibility(screen, callerEmail, all);
    if (!loadingViewRef.current) setActiveViewId(null);
  }, [screen, callerEmail, columns]);

  const hideAllColumns = useCallback(() => {
    const none = {};
    for (const col of columns) {
      none[col.key] = false;
    }
    setColumnVisibility(none);
    saveColumnVisibility(screen, callerEmail, none);
    if (!loadingViewRef.current) setActiveViewId(null);
  }, [screen, callerEmail, columns]);

  /* ── Pending changes (dirty state) ──────────────────────────────────── */
  const [pendingChanges, setPendingChangesRaw] = useState(() => new Map());

  const setPendingChange = useCallback((toolId, field, newValue) => {
    setPendingChangesRaw(prev => {
      const next = new Map(prev);
      const existing = { ...(next.get(toolId) || {}) };

      if (newValue === undefined) {
        delete existing[field];
        if (Object.keys(existing).length === 0) {
          next.delete(toolId);
        } else {
          next.set(toolId, existing);
        }
      } else {
        existing[field] = newValue;
        next.set(toolId, existing);
      }
      return next;
    });
  }, []);

  const clearPendingChanges = useCallback(() => {
    setPendingChangesRaw(new Map());
  }, []);

  const isDirty = useMemo(() => pendingChanges.size > 0, [pendingChanges]);

  /* ── Sort state (session-scoped, not persisted) ────────────────────── */
  const [sortKeys, setSortKeysRaw] = useState([]);

  const setSortKeys = useCallback((keys) => {
    setSortKeysRaw(keys);
    if (!loadingViewRef.current) setActiveViewId(null);
  }, []);

  /* ── Saved views ────────────────────────────────────────────────────── */
  const [savedViews, setSavedViewsRaw] = useState(() => loadSavedViews(screen, callerEmail));
  const [activeViewId, setActiveViewId] = useState(null);

  const activeViewName = useMemo(() => {
    if (!activeViewId) return null;
    const view = savedViews.find(v => v.id === activeViewId);
    return view?.name || null;
  }, [activeViewId, savedViews]);

  const saveView = useCallback((name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { error: 'Name is required' };

    if (savedViews.some(v => v.name.toLowerCase() === trimmed.toLowerCase())) {
      return { error: 'A view with that name already exists' };
    }

    const newView = {
      id: crypto.randomUUID(),
      name: trimmed,
      screen,
      columnVisibility: { ...columnVisibility },
      sortKeys: [...sortKeys],
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedViews, newView];
    setSavedViewsRaw(updated);
    persistSavedViews(screen, callerEmail, updated);
    setActiveViewId(newView.id);
    return { view: newView };
  }, [savedViews, screen, callerEmail, columnVisibility, sortKeys]);

  const deleteView = useCallback((id) => {
    const updated = savedViews.filter(v => v.id !== id);
    setSavedViewsRaw(updated);
    persistSavedViews(screen, callerEmail, updated);
    if (activeViewId === id) setActiveViewId(null);
  }, [savedViews, screen, callerEmail, activeViewId]);

  const loadView = useCallback((view) => {
    loadingViewRef.current = true;
    if (view.columnVisibility) {
      setColumnVisibility(view.columnVisibility);
      saveColumnVisibility(screen, callerEmail, view.columnVisibility);
    }
    if (view.sortKeys) {
      setSortKeysRaw(view.sortKeys);
    }
    setActiveViewId(view.id);
    loadingViewRef.current = false;
  }, [screen, callerEmail]);

  return {
    activeMode,
    setActiveMode,
    columnVisibility,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    pendingChanges,
    setPendingChange,
    clearPendingChanges,
    isDirty,
    sortKeys,
    setSortKeys,
    savedViews,
    activeViewId,
    activeViewName,
    saveView,
    deleteView,
    loadView,
  };
}
